import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { PageHeader, Loading } from "../components/Layout";
import { UserMinus, ArrowRightLeft, CalendarClock, ShieldCheck, AlertTriangle, Users2, Mail } from "lucide-react";

function LeadRow({ lead, onReassign }) {
  const [rep, setRep] = useState("");
  const [busy, setBusy] = useState(false);
  const eligible = lead.eligible_reps.filter((r) => r.covered);
  const go = async () => {
    if (!rep) { toast.error("Pick a covered rep"); return; }
    setBusy(true);
    await onReassign([{ lead_id: lead.id, new_rep_id: rep }]);
    setBusy(false);
  };
  return (
    <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#DFDAD0] last:border-0 items-center" data-testid={`offboard-lead-${lead.id}`}>
      <div className="col-span-4 text-sm text-[#1C2530]">{lead.company}</div>
      <div className="col-span-2 font-mono text-[10px] uppercase text-[#1C2530]/60">{lead.region}</div>
      <div className="col-span-2 font-mono text-[10px] text-[#1C2530]/70">{lead.stage} · {lead.total_score}</div>
      <div className="col-span-4 flex items-center justify-end gap-2">
        <select value={rep} onChange={(e) => setRep(e.target.value)} data-testid={`offboard-rep-select-${lead.id}`}
          className="flex-1 border border-[#DFDAD0] px-2 py-1.5 text-xs bg-white outline-none focus:border-[#1C2530]">
          <option value="">{eligible.length ? "Choose covered rep…" : "No covered rep for region"}</option>
          {eligible.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <button onClick={go} disabled={busy || !eligible.length} data-testid={`offboard-reassign-btn-${lead.id}`}
          className="flex items-center gap-1 border border-[#3E8F99] text-[#3E8F99] px-2 py-1.5 font-mono text-[9px] uppercase hover:bg-[#3E8F99]/10 disabled:opacity-40">
          <ArrowRightLeft className="w-3 h-3" /> Move
        </button>
      </div>
    </div>
  );
}

export default function Offboarding() {
  const [queue, setQueue] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [splitTargets, setSplitTargets] = useState([]);
  const [users, setUsers] = useState([]);
  const [netOwner, setNetOwner] = useState("");
  const [busy, setBusy] = useState(false);

  const loadQueue = () => api.get("/offboarding").then((r) => setQueue(r.data));
  const loadDetail = (id) => api.get(`/offboarding/${id}`).then((r) => setDetail(r.data));
  useEffect(() => { loadQueue(); api.get("/users").then((r) => setUsers(r.data)).catch(() => setUsers([])); }, []);
  useEffect(() => { if (selected) { loadDetail(selected); setNetOwner(""); } else setDetail(null); }, [selected]);

  const reassign = async (assignments) => {
    try {
      const { data } = await api.post(`/offboarding/${selected}/reassign`, { assignments });
      if (data.errors?.length) data.errors.forEach((e) => toast.error(e.error));
      if (data.reassigned?.length) toast.success(`${data.reassigned.length} lead(s) reassigned`);
      await loadDetail(selected); await loadQueue();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const eligiblePool = () => {
    const seen = {};
    (detail?.open_leads || []).forEach((l) => l.eligible_reps.forEach((r) => { if (!seen[r.id]) seen[r.id] = r; }));
    return Object.values(seen);
  };

  const bulkSplit = async () => {
    if (!splitTargets.length) { toast.error("Pick reps to split across"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/offboarding/${selected}/bulk-split`, { target_rep_ids: splitTargets });
      toast.success(`${data.reassigned} lead(s) split across ${splitTargets.length} rep(s)`);
      if (data.skipped?.length) toast.warning(`${data.skipped.length} skipped — no covered rep for their region`);
      setSplitTargets([]);
      await loadDetail(selected); await loadQueue();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const reassignNetwork = async () => {
    if (!netOwner) { toast.error("Pick who inherits the connections"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/offboarding/${selected}/reassign-network`, { new_owner_id: netOwner });
      toast.success(`${data.reassigned} connection(s) reassigned to ${data.new_owner}`);
      setNetOwner(""); await loadDetail(selected); await loadQueue();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const complete = async () => {
    if (!window.confirm("Complete offboarding? The account will be revoked and can no longer sign in.")) return;
    try {
      await api.post(`/users/${selected}/offboard/complete`);
      toast.success("Offboarding complete — account revoked");
      setSelected(null); await loadQueue();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remaining = detail?.open_leads?.length ?? 0;
  const netCount = detail?.net_connections ?? 0;
  const notReady = remaining > 0 || netCount > 0;
  const activeReps = (users || []).filter((u) => u.id !== selected && !["departed", "offboarding"].includes(u.status));

  return (
    <>
      <PageHeader eyebrow="ACCESS CONTROL" title="Reassignment Required" hint="Leads still owned by a departing rep. Offboarding can't complete until every open lead here is reassigned to a covered teammate." />
      <div className="p-8 space-y-6" data-testid="offboarding-page">
        {!queue ? <Loading /> : queue.length === 0 ? (
          <div className="border border-[#DFDAD0] bg-white p-8 text-center" data-testid="offboarding-empty">
            <ShieldCheck className="w-8 h-8 text-[#2F7A3E] mx-auto mb-2" />
            <div className="text-sm text-[#1C2530]/70">No reps are currently being offboarded. Start offboarding from User Management.</div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="offboarding-queue">
            {queue.map((r) => (
              <button key={r.id} onClick={() => setSelected(r.id)} data-testid={`offboarding-rep-${r.email}`}
                className={`text-left border px-4 py-3 ${selected === r.id ? "border-[#E50914] bg-[#E50914]/5" : "border-[#DFDAD0] bg-white hover:bg-[#EFECE5]"}`}>
                <div className="flex items-center gap-2 text-sm font-medium text-[#1C2530]"><UserMinus className="w-4 h-4 text-[#B23A2E]" /> {r.name}</div>
                <div className="mt-1 font-mono text-[10px] uppercase text-[#1C2530]/60">
                  {r.open_leads} open lead(s) · {r.upcoming_meetings} meeting(s)
                </div>
              </button>
            ))}
          </div>
        )}

        {selected && !detail && <Loading />}
        {detail && (
          <div className="space-y-5" data-testid="offboarding-detail">
            <div className="flex items-center justify-between border border-[#DFDAD0] bg-white p-4">
              <div>
                <div className="text-sm font-medium text-[#1C2530]">{detail.rep.name} · {detail.rep.email}</div>
                <div className="mt-0.5 font-mono text-[10px] uppercase text-[#1C2530]/60">
                  {!notReady ? "All leads & connections reassigned — ready to complete"
                    : `${remaining} open lead(s) · ${netCount} connection(s) still to reassign`}
                </div>
              </div>
              <button onClick={complete} disabled={notReady} data-testid="offboard-complete-btn"
                className="flex items-center gap-2 bg-[#2F7A3E] text-white px-4 py-2 text-sm font-medium hover:bg-[#256030] disabled:opacity-40 disabled:cursor-not-allowed">
                <ShieldCheck className="w-4 h-4" /> Complete Offboarding
              </button>
            </div>

            <div className="border border-[#DFDAD0] bg-white p-4" data-testid="offboard-network-panel">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-2">
                <Users2 className="w-3.5 h-3.5" /> Warm Network & LinkedIn connections (tenant-owned — retained, never deleted)
              </div>
              <div className="flex items-center gap-2 text-xs text-[#1C2530]/70 mb-3">
                <Mail className="w-3.5 h-3.5 text-[#3E8F99]" />
                Mailbox connection {detail.mailbox_connected ? "will be revoked on completion" : "not connected"}.
              </div>
              {netCount === 0 ? (
                <div className="text-sm text-[#1C2530]/50">No warm-network or uploaded LinkedIn connections owned by this rep.</div>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-[#1C2530]" data-testid="offboard-net-count">{netCount} connection(s) to reassign to:</span>
                  <select value={netOwner} onChange={(e) => setNetOwner(e.target.value)} data-testid="offboard-net-owner-select"
                    className="border border-[#DFDAD0] px-2 py-1.5 text-xs bg-white outline-none focus:border-[#1C2530]">
                    <option value="">Choose active teammate…</option>
                    {activeReps.map((u) => <option key={u.id} value={u.id}>{u.name}{u.role ? ` · ${u.role}` : ""}</option>)}
                  </select>
                  <button onClick={reassignNetwork} disabled={busy || !netOwner} data-testid="offboard-net-reassign-btn"
                    className="flex items-center gap-1 bg-[#1C2530] text-[#F5F3EE] px-3 py-1.5 font-mono text-[9px] uppercase hover:bg-[#2a3644] disabled:opacity-40">
                    <ArrowRightLeft className="w-3 h-3" /> Reassign all
                  </button>
                </div>
              )}
            </div>

            {remaining > 0 && (
              <div className="border border-[#DFDAD0] bg-white p-4" data-testid="bulk-split-panel">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-3">
                  <ArrowRightLeft className="w-3.5 h-3.5" /> Bulk-split remaining leads (coverage-filtered, round-robin)
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {eligiblePool().map((r) => (
                    <button key={r.id} onClick={() => setSplitTargets((s) => s.includes(r.id) ? s.filter((x) => x !== r.id) : [...s, r.id])}
                      data-testid={`split-target-${r.id}`}
                      className={`px-3 py-1.5 font-mono text-[10px] border ${splitTargets.includes(r.id) ? "bg-[#1C2530] text-[#F5F3EE] border-[#1C2530]" : "border-[#DFDAD0] text-[#1C2530]/70 hover:bg-[#EFECE5]"}`}>
                      {r.name}
                    </button>
                  ))}
                </div>
                <button onClick={bulkSplit} disabled={busy || !splitTargets.length} data-testid="bulk-split-btn"
                  className="bg-[#E50914] text-white px-4 py-2 text-sm font-medium hover:bg-[#c40810] disabled:opacity-40">
                  {busy ? "Splitting…" : `Split ${remaining} lead(s) across ${splitTargets.length || 0} rep(s)`}
                </button>
              </div>
            )}

            <div className="border border-[#DFDAD0] bg-white" data-testid="offboard-leads-table">
              <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#DFDAD0] bg-[#EFECE5] font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">
                <div className="col-span-4">Company</div>
                <div className="col-span-2">Region</div>
                <div className="col-span-2">Stage · Score</div>
                <div className="col-span-4 text-right">Reassign (covered reps only)</div>
              </div>
              {remaining === 0
                ? <div className="px-4 py-6 text-center text-sm text-[#1C2530]/50">No open leads remain assigned.</div>
                : detail.open_leads.map((l) => <LeadRow key={l.id} lead={l} onReassign={reassign} />)}
            </div>

            {detail.upcoming_meetings.length > 0 && (
              <div className="border border-[#DFDAD0] bg-white p-4" data-testid="offboard-meetings">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-2">
                  <CalendarClock className="w-3.5 h-3.5" /> Upcoming meetings (move with their lead)
                </div>
                {detail.upcoming_meetings.map((m) => (
                  <div key={m.id} className="flex items-center gap-2 text-xs text-[#1C2530]/70 py-1">
                    <AlertTriangle className="w-3 h-3 text-[#E2711D]" /> {m.company} — {new Date(m.slot).toLocaleString()}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
